<template>
  <div class="sdb">
    <!-- Header: title + period + filter access -->
    <header class="sdb__header">
      <div class="sdb__heading">
        <h1 class="sdb__title">{{ $t('mobileDash.title') }}</h1>
        <div class="sdb__range">{{ rangeLabel }}</div>
      </div>
      <button type="button" class="sdb__pill" @click="sheetOpen = true">
        <span>{{ presetLabel }}</span>
        <i class="fa-solid fa-chevron-down"></i>
        <span v-if="activeFilterCount" class="sdb__badge">{{ activeFilterCount }}</span>
      </button>
    </header>

    <!-- Active filter chips -->
    <div v-if="chips.length" class="sdb__chips">
      <button v-for="chip in chips" :key="chip.key" type="button" class="sdb__chip" @click="removeChip(chip)">
        {{ chip.label }} <i class="fa-solid fa-xmark"></i>
      </button>
    </div>

    <!-- Refresh indicator: filter changes keep the old data visible but dimmed -->
    <div v-if="refreshing" class="sdb__updating">
      <b-spinner small/> {{ $t('mobileDash.updating') }}
    </div>

    <!-- KPIs -->
    <md-section :class="{ 'sdb__dim': summaryLoading && summary }" :error="summaryError" @retry="fetchSummary">
      <md-skeleton v-if="summaryLoading && !summary" variant="kpi"/>
      <template v-else-if="summary">
        <div v-if="isEmpty" class="sdb__empty">
          <i class="fa-regular fa-folder-open"></i>
          <div class="sdb__empty-title">{{ $t('mobileDash.emptyTitle') }}</div>
          <div class="sdb__empty-text">{{ $t('mobileDash.emptyText') }}</div>
        </div>
        <template v-else>
          <md-primary-kpi
              :value="fullEuro(current.netDeposits)"
              :label="$t('mobileDash.totalNet')"
          />
          <div class="sdb__grid">
            <md-kpi-card :label="$t('mobileDash.deposits')" :value="fullEuro(current.totalDeposits)" tone="deposit"
                         tappable @open="openKpiDrill('deposits')"/>
            <md-kpi-card :label="$t('mobileDash.withdrawals')" :value="fullEuro(absWithdrawals(current))" tone="withdraw"
                         tappable @open="openKpiDrill('withdrawals')"/>
            <md-kpi-card :label="$t('mobileDash.ftd')" :value="wholeNumber(current.ftdCount)" tone="ftd"
                         tappable @open="openKpiDrill('ftd')"/>
            <md-kpi-card :label="$t('mobileDash.ftdAmount')" :value="fullEuro(current.ftdAmount)" tone="ftd"
                         tappable @open="openKpiDrill('ftd')"/>
            <md-kpi-card :label="$t('mobileDash.std')" :value="wholeNumber(current.stdCount)" tone="std"
                         tappable @open="openKpiDrill('std')"/>
            <md-kpi-card :label="$t('mobileDash.newClients')" :value="wholeNumber(current.clientsCount)" tone="clients"/>
            <!-- Conversion card removed per request — restore:
                 <md-kpi-card :label="$t('mobileDash.conversion')" :value="conversionLabel" tone="conv"/> -->
          </div>
        </template>
      </template>
    </md-section>

    <!-- Desks: scoreboard cards, ALWAYS grouped by desk (like the affiliate
         dashboard's desk grouping). Tapping a desk opens its per-agent sheet
         with a Sales/Retention switch — the agents no longer live on the page. -->
    <md-section v-if="!rankingsForbidden && rankings" :class="{ 'sdb__dim': rankingsLoading }" :title="$t('mobileDash.byDesk')">
      <md-affiliate-list :rows="(rankings || {}).desks || []" :limit="5" @select="openDeskAgents"/>
    </md-section>

    <!-- Sales vs Retention (hidden when a single department is filtered) -->
    <md-section v-if="summary && !isEmpty && !filter.department" :class="{ 'sdb__dim': summaryLoading }" :title="$t('mobileDash.byDepartment')">
      <md-split-bar :parts="departmentParts"/>
    </md-section>

    <!-- Rankings -->
    <md-section
        v-if="!rankingsForbidden"
        :class="{ 'sdb__dim': rankingsLoading && rankings }"
        :title="$t('mobileDash.performance')"
        :error="rankingsError"
        @retry="fetchRankings"
    >
      <md-skeleton v-if="rankingsLoading && !rankings" variant="list"/>
      <template v-else-if="rankings">
        <div class="sdb__tabs">
          <button
              v-for="tab in rankingTabs" :key="tab.key"
              type="button"
              class="sdb__tab"
              :class="{ 'is-active': rankingTab === tab.key }"
              @click="rankingTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>
        <md-rank-list :items="rankingItems" :limit="5" selectable @select="openRankingDrill"/>
      </template>
    </md-section>

    <!-- Trend -->
    <md-section v-if="summary && !isEmpty" :class="{ 'sdb__dim': summaryLoading }" :title="$t('mobileDash.trend')">
      <md-trend-chart :points="summary.series || []" :is-dark-skin="isDarkSkin"/>
    </md-section>

    <!-- Insights — disabled for now per request; flip SHOW_INSIGHTS to bring it back
         (the computed, MdInsights component and locales are all still in place). -->
    <md-section v-if="showInsights && insights.length" :class="{ 'sdb__dim': summaryLoading || rankingsLoading }" :title="$t('mobileDash.insights')">
      <md-insights :insights="insights"/>
    </md-section>

    <md-filter-sheet :visible="sheetOpen" :value="filter" @close="sheetOpen = false" @input="onFilterApplied"/>
    <md-agent-sheet :visible="deskSheet.visible" :desk="deskSheet.desk" :filters="filter"
                    @close="deskSheet.visible = false" @select="onDeskAgentSelect"/>
    <md-drill-sheet :visible="drill.visible" :title="drill.title" :filters="drill.filters" @close="drill.visible = false"/>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import MdSection from '@/components/mobile/MdSection';
import MdSkeleton from '@/components/mobile/MdSkeleton';
import MdPrimaryKpi from '@/components/mobile/MdPrimaryKpi';
import MdKpiCard from '@/components/mobile/MdKpiCard';
import MdTrendChart from '@/components/mobile/MdTrendChart';
import MdSplitBar from '@/components/mobile/MdSplitBar';
import MdRankList from '@/components/mobile/MdRankList';
import MdAffiliateList from '@/components/mobile/MdAffiliateList';
import MdInsights from '@/components/mobile/MdInsights';
import MdFilterSheet, { defaultFilter } from '@/components/mobile/MdFilterSheet';
import MdDrillSheet from '@/components/mobile/MdDrillSheet';
import MdAgentSheet from '@/components/mobile/MdAgentSheet';
import { fullEuro, wholeNumber, pct, deltaPct } from '@/components/mobile/mdFormat';

export default {
  name: 'MobileSuperDashboard',
  components: {
    MdSection, MdSkeleton, MdPrimaryKpi, MdKpiCard, MdTrendChart,
    MdSplitBar, MdRankList, MdAffiliateList, MdInsights, MdFilterSheet, MdDrillSheet, MdAgentSheet,
  },
  mixins: [mixinBase, mixinNotifications],
  data() {
    return {
      // Insights section is parked, not deleted — set true to re-enable.
      showInsights: false,
      filter: defaultFilter(this.$moment),
      sheetOpen: false,
      summary: null,
      summaryLoading: false,
      summaryError: null,
      rankings: null,
      rankingsLoading: false,
      rankingsError: null,
      rankingsForbidden: false,
      rankingTab: 'organizations',
      deskSheet: { visible: false, desk: '' },
      drill: { visible: false, title: '', filters: {} },
    };
  },
  computed: {
    ...mapGetters('data', ['getOrganizationNameById', 'getBusinessUnitNameById', 'allOrganizations', 'allBusinessUnits']),
    ...mapGetters('mobileDashboard', { storedFilter: 'filter' }),
    isDarkSkin() {
      return this.$store.state.appConfig.layout.skin === 'dark';
    },
    // True on RE-loads only (data already on screen) — first loads show skeletons
    refreshing() {
      return (this.summaryLoading && this.summary != null)
          || (this.rankingsLoading && this.rankings != null);
    },
    current() {
      return (this.summary && this.summary.current) || {};
    },
    previous() {
      return (this.summary && this.summary.previous) || {};
    },
    isEmpty() {
      if (!this.summary) return false;
      const c = this.current;
      return !(c.totalDeposits || 0) && !(c.ftdCount || 0) && !(c.clientsCount || 0)
          && !Math.abs(c.totalWithdrawals || 0);
    },
    rangeLabel() {
      const range = this.filter.range || {};
      if (!range.from || !range.until) return '';
      const from = this.$moment(range.from);
      const until = this.$moment(range.until);
      const sameYear = from.year() === until.year();
      return `${from.format('MMM D')} – ${until.format(sameYear ? 'MMM D' : 'MMM D, YYYY')}`;
    },
    presetLabel() {
      if (this.filter.preset === 'custom') return this.$t('mobileDash.customRange');
      return this.$t(`filters.picker.${this.filter.preset || 'thisMonth'}`);
    },
    activeFilterCount() {
      let count = 0;
      if (this.filter.department) count++;
      if ((this.filter.desks || []).length) count++;
      if ((this.filter.orgIds || []).length) count++;
      if ((this.filter.buIds || []).length) count++;
      return count;
    },
    chips() {
      const chips = [];
      if (this.filter.department) {
        chips.push({ key: 'department', label: this.$t(`mobileDash.${this.filter.department.toLowerCase()}`) });
      }
      (this.filter.desks || []).forEach(desk => chips.push({ key: `desk:${desk}`, label: desk }));
      (this.filter.orgIds || []).forEach(id =>
          chips.push({ key: `org:${id}`, label: this.getOrganizationNameById(id) || id }));
      (this.filter.buIds || []).forEach(id =>
          chips.push({ key: `bu:${id}`, label: this.getBusinessUnitNameById(id) || id }));
      return chips;
    },
    withdrawalsDelta() {
      return deltaPct(Math.abs(this.current.totalWithdrawals || 0), Math.abs(this.previous.totalWithdrawals || 0) || null);
    },
    conversionLabel() {
      return pct(this.conversionOf(this.current));
    },
    conversionDelta() {
      return deltaPct(this.conversionOf(this.current), this.conversionOf(this.previous));
    },
    departmentParts() {
      return [
        { label: this.$t('mobileDash.sales'), value: this.current.salesDeposits || 0, color: '#7367f0' },
        { label: this.$t('mobileDash.retention'), value: this.current.retentionDeposits || 0, color: '#28c76f' },
      ];
    },
    rankingTabs() {
      // Desks got their own always-on section above — this one splits the
      // rest: by organization (default) and by business unit.
      return [
        { key: 'organizations', label: this.$t('mobileDash.tabs.organizations') },
        { key: 'businessUnits', label: this.$t('mobileDash.tabs.businessUnits') },
      ];
    },
    rankingItems() {
      const rows = (this.rankings || {})[this.rankingTab] || [];
      return rows.map(row => ({
        key: row.key,
        label: row.label,
        stats: this.rowStats(row),
        value: fullEuro(row.netTotal),
        raw: row,
      }));
    },
    insights() {
      if (!this.summary || this.isEmpty) return [];
      const out = [];
      const netDelta = this.delta('netDeposits');
      const ftdDelta = this.delta('ftdCount');
      if (netDelta != null && netDelta >= 10) {
        out.push({
          tone: 'up', icon: 'fa-solid fa-arrow-trend-up',
          title: this.$t('mobileDash.insight.growthTitle'),
          text: this.$t('mobileDash.insight.growthText', { pct: netDelta.toFixed(1) }),
        });
      } else if (netDelta != null && netDelta <= -10) {
        out.push({
          tone: 'down', icon: 'fa-solid fa-arrow-trend-down',
          title: this.$t('mobileDash.insight.declineTitle'),
          text: this.$t('mobileDash.insight.declineText', { pct: Math.abs(netDelta).toFixed(1) }),
        });
      }
      if (ftdDelta != null && ftdDelta <= -10) {
        out.push({
          tone: 'down', icon: 'fa-solid fa-user-minus',
          title: this.$t('mobileDash.insight.ftdDropTitle'),
          text: this.$t('mobileDash.insight.ftdDropText', { pct: Math.abs(ftdDelta).toFixed(1) }),
        });
      } else if (ftdDelta != null && ftdDelta >= 10) {
        out.push({
          tone: 'up', icon: 'fa-solid fa-user-plus',
          title: this.$t('mobileDash.insight.ftdUpTitle'),
          text: this.$t('mobileDash.insight.ftdUpText', { pct: ftdDelta.toFixed(1) }),
        });
      }
      const topDesk = (((this.rankings || {}).desks) || [])[0];
      if (topDesk && topDesk.netTotal > 0) {
        out.push({
          tone: 'star', icon: 'fa-solid fa-trophy',
          title: this.$t('mobileDash.insight.topDeskTitle'),
          text: this.$t('mobileDash.insight.topDeskText', { desk: topDesk.label, net: fullEuro(topDesk.netTotal) }),
        });
      }
      const topAffiliate = (((this.rankings || {}).affiliates) || []).filter(a => a.key !== 'duplicated')[0];
      if (topAffiliate && topAffiliate.ftds > 0) {
        out.push({
          tone: 'info', icon: 'fa-solid fa-handshake',
          title: this.$t('mobileDash.insight.topAffiliateTitle'),
          text: this.$t('mobileDash.insight.topAffiliateText', { affiliate: topAffiliate.label, count: topAffiliate.ftds }),
        });
      }
      return out.slice(0, 4);
    },
  },
  created() {
    if (this.storedFilter) {
      this.filter = JSON.parse(JSON.stringify(this.storedFilter));
    } else {
      this.setFilter(JSON.parse(JSON.stringify(this.filter)));
    }
    this.fetchAll();
  },
  methods: {
    ...mapActions('mobileDashboard', ['getSummary', 'getRankings']),
    ...mapMutations('mobileDashboard', ['setFilter']),
    fullEuro,
    wholeNumber,
    conversionOf(kpis) {
      if (!kpis || !kpis.clientsCount) return null;
      return ((kpis.ftdCount || 0) / kpis.clientsCount) * 100;
    },
    absWithdrawals(kpis) {
      return Math.abs(kpis.totalWithdrawals || 0);
    },
    delta(field) {
      return deltaPct(this.current[field], this.previous[field]);
    },
    // Desk/brand/unit rows: counts only — conversion lives in the affiliate section
    rowStats(row) {
      return [
        { value: row.ftds || 0, label: 'FTD', tone: 'ftd' },
        { value: row.leads || 0, label: this.$t('mobileDash.leadsLabel'), tone: 'leads' },
      ];
    },

    // ---------------------------------------------------------- drill-down
    /**
     * Mirrors the desktop SalesDashboard kpiTxFilters(): the drill list shows
     * the same transactions the KPI counted. Desk/department are resolved to
     * org/BU ids client-side, matching the backend's own scoping.
     */
    drillScope() {
      const selectedOrgIds = (this.filter.orgIds && this.filter.orgIds.length) ? this.filter.orgIds : null;
      let orgIds = selectedOrgIds;
      if ((this.filter.desks || []).length) {
        const desks = new Set(this.filter.desks);
        const deskOrgIds = (this.allOrganizations || [])
            .filter(org => desks.has(org.desk && org.desk.length ? org.desk : 'No Desk'))
            .map(org => org.id);
        orgIds = selectedOrgIds ? deskOrgIds.filter(id => selectedOrgIds.includes(id)) : deskOrgIds;
        if (!orgIds.length) orgIds = ['0'];
      }
      let buIds = (this.filter.buIds && this.filter.buIds.length) ? this.filter.buIds : null;
      if (this.filter.department) {
        const typed = (this.allBusinessUnits || [])
            .filter(bu => bu.type === this.filter.department)
            .filter(bu => !orgIds || orgIds.includes(bu.orgId))
            .map(bu => bu.id)
            .filter(id => !buIds || buIds.includes(id));
        buIds = typed.length ? typed : ['0'];
      }
      return { orgIds, businessUnit: buIds };
    },
    kpiTxFilters(extra) {
      const filters = {
        type: 'DEPOSIT',
        action: ['CREDIT_LINE', 'TAXES'],
        status: ['APPROVED'],
        isTest: false,
        realType: ['REAL'],
        ...this.drillScope(),
        ...(extra || {}),
      };
      const range = this.filter.range || {};
      if (range.from) {
        filters.registrationTime = {
          start: this.$moment(range.from).startOf('day').toServerDateTimeFormat(),
          end: this.$moment(range.until || range.from).endOf('day').toServerDateTimeFormat(),
        };
      }
      return filters;
    },
    openDrill(title, extra) {
      this.drill = { visible: true, title, filters: this.kpiTxFilters(extra) };
    },
    openKpiDrill(kind) {
      if (kind === 'ftd') {
        this.openDrill(this.$t('mobileDash.ftd'), { ftdFamily: true, excludeRefundedFtdClients: true, action: ['CREDIT_LINE'] });
      } else if (kind === 'std') {
        this.openDrill(this.$t('mobileDash.std'), { isStd: true, action: ['CREDIT_LINE'] });
      } else if (kind === 'withdrawals') {
        this.openDrill(this.$t('mobileDash.withdrawals'), { type: 'WITHDRAWAL', action: ['CREDIT_LINE'] });
      } else {
        this.openDrill(this.$t('mobileDash.deposits'), {});
      }
    },
    // Desk card → the per-agent sheet (Sales/Retention inside).
    openDeskAgents(row) {
      const raw = row.raw || row || {};
      const desk = raw.label || raw.key;
      if (!desk) return;
      this.deskSheet = { visible: true, desk };
    },
    /** Agent tapped inside the desk sheet → transactions drill scoped to that desk + agent. */
    onDeskAgentSelect(item) {
      const agent = item.raw || {};
      if (!agent.userId) return;
      const extra = agent.kind === 'sales'
          ? { ftdFamily: true, excludeRefundedFtdClients: true, action: ['CREDIT_LINE'], userIds: [agent.userId] }
          : { isFtd: false, action: ['CREDIT_LINE'], userIds: [agent.userId] };
      this.openDrill(`${this.deskSheet.desk} · ${item.label}`, { ...extra, orgIds: this.deskOrgIds(this.deskSheet.desk) });
    },
    /** Client-side desk → orgIds resolution (same rule as the backend's deskOf). */
    deskOrgIds(desk) {
      const ids = (this.allOrganizations || [])
          .filter(org => (org.desk && org.desk.length ? org.desk : 'No Desk') === desk)
          .map(org => org.id);
      return ids.length ? ids : ['0'];
    },
    openRankingDrill(item) {
      const row = item.raw || {};
      if (this.rankingTab === 'businessUnits') {
        this.openDrill(item.label, { businessUnit: [row.key] });
      } else {
        this.openDrill(item.label, { orgIds: [row.key] });
      }
    },
    onFilterApplied(filter) {
      this.filter = filter;
      this.setFilter(JSON.parse(JSON.stringify(filter)));
      this.fetchAll();
    },
    removeChip(chip) {
      const filter = JSON.parse(JSON.stringify(this.filter));
      if (chip.key === 'department') filter.department = null;
      else if (chip.key.startsWith('desk:')) filter.desks = filter.desks.filter(d => `desk:${d}` !== chip.key);
      else if (chip.key.startsWith('org:')) filter.orgIds = filter.orgIds.filter(id => `org:${id}` !== chip.key);
      else if (chip.key.startsWith('bu:')) filter.buIds = filter.buIds.filter(id => `bu:${id}` !== chip.key);
      this.onFilterApplied(filter);
    },
    fetchAll() {
      this.fetchSummary();
      this.fetchRankings();
    },
    fetchSummary() {
      this.summaryLoading = true;
      this.summaryError = null;
      this.getSummary(this.filter)
          .then(summary => {
            this.summary = summary;
          })
          .catch(err => {
            this.summaryError = this.$t('mobileDash.summaryError');
            console.error('mobile summary error', err, this.getErrorDescription(err));
          })
          .finally(() => {
            this.summaryLoading = false;
          });
    },
    fetchRankings() {
      if (this.rankingsForbidden) return;
      this.rankingsLoading = true;
      this.rankingsError = null;
      this.getRankings(this.filter)
          .then(rankings => {
            this.rankings = rankings;
          })
          .catch(err => {
            // No affiliate permission → hide the sections instead of erroring
            if (err.response && err.response.status === 403) {
              this.rankingsForbidden = true;
            } else {
              this.rankingsError = this.$t('mobileDash.rankingsError');
            }
          })
          .finally(() => {
            this.rankingsLoading = false;
          });
    },
  },
};
</script>

<style scoped>
.sdb {
  max-width: 520px;
  margin: 0 auto;
  padding: 0.25rem 0.15rem 2rem;
}
.sdb__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
}
.sdb__title {
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0;
  color: var(--md-strong, #24292f);
}
/* Small accent bar under the title — readable everywhere, still distinctive */
.sdb__title::after {
  content: '';
  display: block;
  width: 34px;
  height: 3px;
  margin-top: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, #7367f0, #00cfe8);
}
.dark-layout .sdb__title {
  color: #d0d2d6;
}
.sdb__range {
  font-size: 12px;
  color: var(--md-muted, #8a919c);
  margin-top: 1px;
}
.sdb__pill {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid rgba(34, 41, 47, 0.12);
  background: var(--md-card-bg, #fff);
  border-radius: 999px;
  padding: 0.45rem 0.9rem;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  transition: transform 0.08s ease;
}
.sdb__pill:active { transform: scale(0.96); }
.sdb__pill i { font-size: 10px; opacity: 0.6; }
.sdb__badge {
  position: absolute;
  top: -5px;
  right: -4px;
  min-width: 17px;
  height: 17px;
  border-radius: 999px;
  background: var(--primary, #7367f0);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}
.sdb__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
}
.sdb__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: rgba(115, 103, 240, 0.1);
  color: var(--primary, #7367f0);
  border-radius: 999px;
  padding: 0.25rem 0.65rem;
  font-size: 11.5px;
  font-weight: 600;
}
.sdb__chip i { font-size: 9px; opacity: 0.7; }
.sdb__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
}
.sdb__tabs {
  display: inline-flex;
  gap: 2px;
  margin-bottom: 0.5rem;
  padding: 3px;
  background: rgba(138, 145, 156, 0.1);
  border-radius: 8px;
}
.sdb__tab {
  border: none;
  background: transparent;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--md-muted, #8a919c);
}
.sdb__tab.is-active {
  background: var(--md-card-bg, #fff);
  color: var(--md-strong, #24292f);
  box-shadow: 0 1px 3px rgba(34, 41, 47, 0.12);
}
.sdb__dim {
  opacity: 0.45;
  pointer-events: none;
  transition: opacity 0.2s ease;
}
.sdb__updating {
  position: fixed;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1080;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--md-card-bg, #fff);
  border: 1px solid rgba(34, 41, 47, 0.1);
  box-shadow: 0 4px 14px rgba(34, 41, 47, 0.16);
  border-radius: 999px;
  padding: 0.35rem 0.9rem;
  font-size: 12px;
  font-weight: 600;
  animation: sdb-updating-in 0.2s ease;
}
@keyframes sdb-updating-in {
  from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}
.dark-layout .sdb__updating {
  --md-card-bg: #283046;
  border-color: #3b4253;
  color: #d0d2d6;
}

.sdb__empty {
  text-align: center;
  padding: 2.2rem 1rem;
  border: 1px dashed rgba(138, 145, 156, 0.35);
  border-radius: 12px;
}
.sdb__empty i {
  font-size: 26px;
  color: var(--md-muted, #8a919c);
  opacity: 0.7;
}
.sdb__empty-title {
  margin-top: 0.5rem;
  font-size: 14px;
  font-weight: 700;
}
.sdb__empty-text {
  margin-top: 2px;
  font-size: 12px;
  color: var(--md-muted, #8a919c);
}

.dark-layout .sdb__pill,
.dark-layout .sdb__tab.is-active {
  --md-card-bg: #283046;
  border-color: #3b4253;
  color: #d0d2d6;
}
</style>
